import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { RSpinBlock } from "@/components/RSpin";
import API from "@/lib/api";

const LOOKUP_PAGE_SIZE = 50;

const NETWORK_META = {
  impact: { label: "Impact", cls: "bg-slate-100 text-slate-700" },
  partnerize: { label: "Partnerize", cls: "bg-indigo-50 text-indigo-700" },
  skimlinks: { label: "Skimlinks", cls: "bg-orange-50 text-orange-700" },
  mrge: { label: "mrge", cls: "bg-emerald-50 text-emerald-700" },
  cj: { label: "CJ", cls: "bg-amber-50 text-amber-700" },
  admedia: { label: "AdMedia", cls: "bg-violet-50 text-violet-700" },
  pubadhub: { label: "PubAdHub", cls: "bg-rose-50 text-rose-700" },
  pubadhub_kevin: { label: "PubAdHub 2", cls: "bg-teal-50 text-teal-700" },
  flexoffers: { label: "FlexOffers", cls: "bg-indigo-50 text-indigo-700" },
  shopnomix_kevin: { label: "Shopnomix InMobi", cls: "bg-fuchsia-50 text-fuchsia-700" },
  siteplug: { label: "Siteplug", cls: "bg-sky-50 text-sky-700" },
  maxbounty: { label: "MaxBounty", cls: "bg-cyan-50 text-cyan-700" },
  cashbackhive: { label: "CashbackHive", cls: "bg-teal-50 text-teal-700" },
  giddyup: { label: "GiddyUp", cls: "bg-teal-50 text-teal-700" },
  rga: { label: "ReallyGreatAds", cls: "bg-red-50 text-red-700" },
  shopnomix: { label: "Shopnomix", cls: "bg-lime-50 text-lime-700" },
  pronomix: { label: "Pronomix", cls: "bg-purple-50 text-purple-700" },
};

const EMPTY_NET_COUNTS = {
  all: 0,
  ...Object.fromEntries(Object.keys(NETWORK_META).map((k) => [k, 0])),
};

// Country normalization — Impact stores UPPERCASE names + occasional ISO
// codes; Skimlinks stores Title-Case names ("United States"); mrge stores
// ISO codes ("US"). The cross-network country filter unifies these by
// canonicalizing every value to its ISO-2 code, then matching on that.
const NAME_TO_ISO = {
  "UNITED STATES": "US", "UNITED STATES OF AMERICA": "US", "US": "US", "USA": "US",
  "UNITED KINGDOM": "GB", "UK": "GB", "GREAT BRITAIN": "GB", "GB": "GB",
  "CANADA": "CA", "CA": "CA",
  "AUSTRALIA": "AU", "AU": "AU",
  "GERMANY": "DE", "DE": "DE",
  "FRANCE": "FR", "FR": "FR",
  "SPAIN": "ES", "ES": "ES",
  "ITALY": "IT", "IT": "IT",
  "NETHERLANDS": "NL", "NL": "NL",
  "BELGIUM": "BE", "BE": "BE",
  "SWITZERLAND": "CH", "CH": "CH",
  "AUSTRIA": "AT", "AT": "AT",
  "IRELAND": "IE", "IE": "IE",
  "SWEDEN": "SE", "SE": "SE",
  "NORWAY": "NO", "NO": "NO",
  "DENMARK": "DK", "DK": "DK",
  "FINLAND": "FI", "FI": "FI",
  "PORTUGAL": "PT", "PT": "PT",
  "POLAND": "PL", "PL": "PL",
  "CZECH REPUBLIC": "CZ", "CZ": "CZ",
  "MEXICO": "MX", "MX": "MX",
  "BRAZIL": "BR", "BR": "BR",
  "JAPAN": "JP", "JP": "JP",
  "INDIA": "IN", "IN": "IN",
  "CHINA": "CN", "CN": "CN",
  "SINGAPORE": "SG", "SG": "SG",
  "HONG KONG": "HK", "HK": "HK",
  "UNITED ARAB EMIRATES": "AE", "AE": "AE",
  "SOUTH AFRICA": "ZA", "ZA": "ZA",
  "NEW ZEALAND": "NZ", "NZ": "NZ",
  "KOREA, REPUBLIC OF": "KR", "SOUTH KOREA": "KR", "KR": "KR",
};
const ISO_TO_DISPLAY = {
  US: "United States", GB: "United Kingdom", CA: "Canada", AU: "Australia",
  DE: "Germany", FR: "France", ES: "Spain", IT: "Italy", NL: "Netherlands",
  BE: "Belgium", CH: "Switzerland", AT: "Austria", IE: "Ireland",
  SE: "Sweden", NO: "Norway", DK: "Denmark", FI: "Finland", PT: "Portugal",
  PL: "Poland", CZ: "Czech Republic", MX: "Mexico", BR: "Brazil",
  JP: "Japan", IN: "India", CN: "China", SG: "Singapore", HK: "Hong Kong",
  AE: "United Arab Emirates", ZA: "South Africa", NZ: "New Zealand",
  KR: "South Korea",
};

function _normCountry(raw) {
  if (typeof raw !== "string" || !raw) return null;
  const key = raw.trim().toUpperCase();
  if (NAME_TO_ISO[key]) return NAME_TO_ISO[key];
  // 2-letter codes pass through as-is (covers the 200+ ISO codes we don't
  // have a display name for yet).
  if (/^[A-Z]{2}$/.test(key)) return key;
  // Strip Impact's run-together "UNITEDSTATES" / "UNITEDKINGDOM" / etc.
  const compact = key.replace(/[^A-Z]/g, "");
  if (NAME_TO_ISO[compact]) return NAME_TO_ISO[compact];
  return null;
}

function _brandCountryCodes(b) {
  const out = new Set();
  // Unified geo source — `geo_locations` is the merged result of
  // upstream sync + admin overrides. Falls back to the legacy fields
  // (`shipping_regions`, `user_geographical_locations`) when a brand
  // hasn't been re-synced since the geo merge migration landed, so
  // existing rows don't suddenly appear blank.
  for (const r of b.geo_locations || []) {
    const c = _normCountry(r);
    if (c) out.add(c);
  }
  if (out.size === 0) {
    for (const r of b.shipping_regions || []) {
      const c = _normCountry(r);
      if (c) out.add(c);
    }
    for (const r of b.user_geographical_locations || []) {
      const c = _normCountry(r);
      if (c) out.add(c);
    }
  }
  return out;
}

/**
 * Searchable advertiser lookup with network tabs, country + min-CPA filters,
 * checkbox multi-select, and a single-click bulk-assign bar.
 *
 * Designed to replace the heavy <Select> dropdown on the Publisher Detail
 * "Assign Brand" card — works fine with 100–5 000 brands client-side.
 */
export default function AdvertiserLookup({
  availableBrands: availableBrandsProp = [],
  publisherId = "",
  refreshKey = 0,
  defaultSplit = 80,
  onAssignSelected,
  onAssignAllFiltered,
  onNetworkChange,
  loading: loadingProp = false,
}) {
  const [network, setNetwork] = useState("all");
  // Surface the network selection up to the parent (AdminPublisherDetail)
  // so siblings like the Smart Add banner can scope to the currently
  // active network instead of re-querying their own state.
  useEffect(() => { onNetworkChange?.(network); }, [network, onNetworkChange]);
  // Switching network pills is a new context — drop stale selections from
  // the previous feed so counts / header checkbox stay in sync.
  useEffect(() => { setSelectedIds(new Set()); }, [network]);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("ALL");
  const [minCpa, setMinCpa] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [ourPct, setOurPct] = useState("");
  const [pubPct, setPubPct] = useState("");
  const [splitPct, setSplitPct] = useState("");
  // Payout mode for Impact / Partnerize assignments:
  //   fixed_pct  = classic Our% × Pub% × Split cap math (default)
  //   flat_cpa   = pub earns min(pub_flat_amount, network_payout)
  //   passthrough = pub earns network_payout × split on whatever Impact pays
  //                 (used for "Up to X%" variable-rate offers)
  // Skimlinks always forces passthrough regardless of this state.
  const [payoutMode, setPayoutMode] = useState("fixed_pct");
  // CPC mode: optional publisher-facing display label (e.g. "upto $0.25").
  // Stored on every CPC assignment in this batch — empty string is OK,
  // the publisher just sees "CPC" with no extra text.
  const [cpcDisplay, setCpcDisplay] = useState("");
  const [flatAmount, setFlatAmount] = useState("");
  // Passthrough-only: admin enters a per-action breakdown (Online Sale:
  // "35%-45%", Sign-Up: "$0.10", etc.). We parse each row server-side to
  // derive numeric min/max so Omar's ingestion can filter on a real number.
  const [payoutActions, setPayoutActions] = useState([
    { action: "", value: "", note: "" },
  ]);
  const [headlinePayout, setHeadlinePayout] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [page, setPage] = useState(1);
  const [remoteItems, setRemoteItems] = useState([]);
  const [remoteTotal, setRemoteTotal] = useState(0);
  const [remoteLoading, setRemoteLoading] = useState(Boolean(publisherId));
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [netFacets, setNetFacets] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [network, debouncedSearch]);

  useEffect(() => {
    if (!publisherId) {
      setNetFacets(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/admin/brands", {
          params: {
            page: 1,
            page_size: 1,
            fields: "lookup",
            hide_inactive: 1,
            exclude_publisher_id: publisherId,
            network_counts: 1,
          },
        });
        if (cancelled) return;
        const by = data?.by_network || {};
        const next = { ...EMPTY_NET_COUNTS };
        let all = 0;
        for (const [net, raw] of Object.entries(by)) {
          const n = Number(raw || 0);
          all += n;
          if (next[net] !== undefined) next[net] = n;
        }
        next.all = all;
        setNetFacets(next);
      } catch {
        if (!cancelled) setNetFacets(null);
      }
    })();
    return () => { cancelled = true; };
  }, [publisherId, refreshKey]);

  useEffect(() => {
    if (!publisherId) return undefined;
    let cancelled = false;
    (async () => {
      setRemoteLoading(true);
      try {
        const params = {
          page,
          page_size: LOOKUP_PAGE_SIZE,
          fields: "lookup",
          hide_inactive: 1,
          exclude_publisher_id: publisherId,
        };
        if (network !== "all") params.network = network;
        if (debouncedSearch) params.q = debouncedSearch;
        const { data } = await API.get("/admin/brands", { params });
        if (!cancelled) {
          setRemoteItems(Array.isArray(data) ? data : (data.items || []));
          setRemoteTotal(Array.isArray(data) ? data.length : (data.total || 0));
        }
      } catch {
        if (!cancelled) {
          setRemoteItems([]);
          setRemoteTotal(0);
        }
      } finally {
        if (!cancelled) setRemoteLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [publisherId, page, network, debouncedSearch, refreshKey]);

  const availableBrands = publisherId ? remoteItems : availableBrandsProp;
  const loading = publisherId ? (remoteLoading && remoteItems.length === 0) : loadingProp;
  const lookupTotalPages = publisherId
    ? Math.max(1, Math.ceil((remoteTotal || 0) / LOOKUP_PAGE_SIZE))
    : 1;

  // Build country list as ISO-2 codes from BOTH shipping_regions
  // (Impact / Skimlinks naming) and user_geographical_locations (mrge ISO
  // codes). All values normalized via _normCountry so "United States",
  // "UNITED STATES", and "US" merge into a single "US" option that
  // actually matches every brand.
  const countries = useMemo(() => {
    const set = new Set();
    for (const b of availableBrands) {
      for (const c of _brandCountryCodes(b)) set.add(c);
    }
    // Sort: known names first (alpha by display label), then unknown ISO
    // codes alpha.
    return Array.from(set).sort((a, b) => {
      const la = ISO_TO_DISPLAY[a];
      const lb = ISO_TO_DISPLAY[b];
      if (la && !lb) return -1;
      if (!la && lb) return 1;
      return (la || a).localeCompare(lb || b);
    });
  }, [availableBrands]);

  // Per-network counts — shown inline on the tab chips.
  const netCounts = useMemo(() => {
    const counts = { ...EMPTY_NET_COUNTS };
    if (publisherId) {
      if (netFacets) return { ...EMPTY_NET_COUNTS, ...netFacets };
      counts[network] = remoteTotal;
      if (network === "all") counts.all = remoteTotal;
      return counts;
    }
    counts.all = availableBrands.length;
    for (const b of availableBrands) {
      const n = b.network || "impact";
      if (counts[n] !== undefined) counts[n] += 1;
    }
    return counts;
  }, [availableBrands, publisherId, network, remoteTotal, netFacets]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const minR = parseFloat(minCpa);
    return availableBrands.filter((b) => {
      const net = b.network || "impact";
      if (network !== "all" && net !== network) return false;
      if (country !== "ALL") {
        const codes = _brandCountryCodes(b);
        if (!codes.has(country)) return false;
      }
      if (Number.isFinite(minR) && (b.base_commission_pct || 0) < minR) return false;
      if (q) {
        const hay = `${b.name || ""} ${b.advertiser_name || ""} ${b.campaign_id || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [availableBrands, network, country, minCpa, search]);

  const filteredIds = useMemo(() => new Set(filtered.map((b) => b.campaign_id)), [filtered]);
  // Cap visible rows so the DOM stays snappy when 25k mrge brands flow in.
  // The full filtered set is still used for "Assign All Filtered" and
  // count display — only the rendered table is sliced.
  const MAX_VISIBLE_ROWS = 500;
  const visible = useMemo(
    () => (filtered.length > MAX_VISIBLE_ROWS ? filtered.slice(0, MAX_VISIBLE_ROWS) : filtered),
    [filtered]
  );

  // Network-lock: once a brand is selected, disable rows from other networks
  // so admins can't mix Impact + Skimlinks in one bulk assign (different
  // math models — we'd need two API calls under the hood).
  const selectedNetwork = useMemo(() => {
    if (selectedIds.size === 0) return null;
    const firstSelected = availableBrands.find((b) => selectedIds.has(b.campaign_id));
    return firstSelected?.network || "impact";
  }, [selectedIds, availableBrands]);

  const isRowDisabled = (b) => {
    if (!selectedNetwork) return false;
    return (b.network || "impact") !== selectedNetwork;
  };

  const eligibleRows = useMemo(
    () => filtered.filter((b) => !isRowDisabled(b)),
    [filtered, selectedNetwork]
  );
  const allVisibleSelected =
    eligibleRows.length > 0 &&
    eligibleRows.every((b) => selectedIds.has(b.campaign_id));

  const toggleOne = (id) => {
    const brand = availableBrands.find((b) => b.campaign_id === id);
    if (brand && isRowDisabled(brand)) return; // locked by network selection
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const eligible = filtered.filter((b) => !isRowDisabled(b));
      const allEligibleSelected = eligible.every((b) => next.has(b.campaign_id));
      if (allEligibleSelected) {
        for (const b of eligible) next.delete(b.campaign_id);
      } else {
        for (const b of eligible) next.add(b.campaign_id);
      }
      return next;
    });
  };

  const resetFilters = () => {
    setNetwork("all");
    setSearch("");
    setCountry("ALL");
    setMinCpa("");
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Skimlinks is pass-through — no per-brand rate inputs, pub earns
  // `network_payout × split` on whatever shape the offer is (flat / % / tiered).
  const activeNetwork = selectedNetwork || (network !== "all" ? network : null);
  const isSkimlinksContext = activeNetwork === "skimlinks";

  // CPC detection — when EVERY currently-selected brand has offer_type=cpc,
  // force the lookup into CPC mode (admin clicked CPC brands explicitly).
  // Mixed CPA+CPC selections fall back to whatever payout_mode the admin
  // picked, and the backend per-brand routing in bulk.py still does the
  // right thing for CPC brands.
  const allSelectedAreCpc = (() => {
    if (selectedIds.size === 0) return false;
    for (const id of selectedIds) {
      const b = availableBrands.find((x) => x.campaign_id === id);
      if (!b || (b.offer_type || "").toLowerCase() !== "cpc") return false;
    }
    return true;
  })();

  // Skimlinks always forces passthrough. CPC-only selection forces cpc.
  // Everyone else honors the radio.
  const effectiveMode = isSkimlinksContext
    ? "passthrough"
    : (allSelectedAreCpc ? "cpc" : payoutMode);

  const validateInputs = () => {
    const split = splitPct === "" ? defaultSplit : parseFloat(splitPct);
    if (!Number.isFinite(split) || split <= 0 || split > 100) {
      return { ok: false, msg: "Split must be between 1 and 100" };
    }
    if (effectiveMode === "cpc") {
      // CPC display label is OPTIONAL — empty string is acceptable.
      // Blank pub flat → null (backend may inherit brand catalog default).
      let flat = null;
      if ((flatAmount || "").trim() !== "") {
        flat = parseFloat(flatAmount);
        if (!Number.isFinite(flat) || flat < 0) {
          return { ok: false, msg: "Pub flat $ must be 0 or greater" };
        }
      }
      return {
        ok: true,
        mode: "cpc",
        our: null,
        pub: null,
        flat,
        split,
        cpc_payout_display: (cpcDisplay || "").trim(),
      };
    }
    if (effectiveMode === "passthrough") {
      // Breakdown is optional — empty assign mirrors each brand's catalog rate.
      const filled = payoutActions.filter(
        (r) => (r.action || "").trim() || (r.value || "").trim() || (r.note || "").trim()
      );
      return {
        ok: true,
        mode: "passthrough",
        our: null,
        pub: null,
        flat: null,
        split,
        payout_actions: filled,
        headline_payout: (headlinePayout || "").trim(),
      };
    }
    if (effectiveMode === "flat_cpa") {
      const flat = parseFloat(flatAmount);
      if (!Number.isFinite(flat) || flat <= 0) {
        return { ok: false, msg: "Enter a flat $ CPA greater than 0" };
      }
      return { ok: true, mode: "flat_cpa", our: null, pub: null, flat, split };
    }
    // fixed_pct
    const pub = parseFloat(pubPct);
    if (!Number.isFinite(pub) || pub <= 0) {
      return { ok: false, msg: "Enter a publisher rate %" };
    }
    return {
      ok: true,
      mode: "fixed_pct",
      our: ourPct === "" ? null : parseFloat(ourPct),
      pub,
      flat: null,
      split,
    };
  };

  const doAssign = async (mode) => {
    const v = validateInputs();
    if (!v.ok) {
      // bubble up via onAssignSelected/onAssignAllFiltered caller's toast
      onAssignSelected?.({ error: v.msg });
      return;
    }
    const ids =
      mode === "selected"
        ? Array.from(selectedIds).filter((id) => filteredIds.has(id))
        : filtered.map((b) => b.campaign_id);
    if (ids.length === 0) {
      onAssignSelected?.({ error: "No brands to assign" });
      return;
    }
    setSubmitting(true);
    try {
      const fn = mode === "selected" ? onAssignSelected : onAssignAllFiltered;
      await fn?.({
        brand_ids: ids,
        base: v.our,
        pub: v.pub,
        split: v.split,
        payout_mode: v.mode,
        pub_flat_amount: v.flat,
        payout_actions: v.payout_actions,
        headline_payout: v.headline_payout,
        cpc_payout_display: v.cpc_payout_display,
      });
      setSelectedIds(new Set());
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = Array.from(selectedIds).filter((id) => filteredIds.has(id)).length;

  if (loading) {
    return (
      <div className="border border-slate-200 rounded-md" data-testid="advertiser-lookup-loading">
        <div className="px-5 py-4 border-b border-slate-200">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-400">
            Advertiser Lookup
          </p>
          <h3 className="font-heading text-base font-semibold text-slate-900 mt-0.5">
            Assign brands to this publisher
          </h3>
        </div>
        <RSpinBlock label="Loading brand catalog…" height={260} />
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-md" data-testid="advertiser-lookup">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-400">
            Advertiser Lookup
          </p>
          <h3 className="font-heading text-base font-semibold text-slate-900 mt-0.5">
            Assign brands to this publisher
          </h3>
        </div>
        <div className="text-xs text-slate-500">
          <span className="font-mono font-semibold text-slate-900">
            {(publisherId ? remoteTotal : filtered.length).toLocaleString()}
          </span>{" "}
          match{(publisherId ? remoteTotal : filtered.length) === 1 ? "" : "es"}
          {publisherId && remoteTotal > LOOKUP_PAGE_SIZE ? ` · page ${page}` : ""}
          {" "}&middot;{" "}
          <span className="font-mono font-semibold text-[#002FA7]">{selectedCount}</span> selected
        </div>
      </div>

      {/* ─── NETWORK TABS + SEARCH ─── */}
      <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
        {[
          { k: "all", label: "All" },
          { k: "impact", label: "Impact" },
          { k: "partnerize", label: "Partnerize" },
          { k: "skimlinks", label: "Skimlinks" },
          { k: "mrge", label: "mrge" },
          { k: "cj", label: "CJ" },
          { k: "admedia", label: "AdMedia" },
          { k: "pubadhub", label: "PubAdHub" },
          { k: "pubadhub_kevin", label: "PubAdHub 2" },
          { k: "flexoffers", label: "FlexOffers" },
          { k: "shopnomix_kevin", label: "Shopnomix InMobi" },
          { k: "siteplug", label: "Siteplug" },
          { k: "maxbounty", label: "MaxBounty" },
          { k: "cashbackhive", label: "CashbackHive" },
          { k: "giddyup", label: "GiddyUp" },
          { k: "rga", label: "ReallyGreatAds" },
          { k: "shopnomix", label: "Shopnomix" },
          { k: "pronomix", label: "Pronomix" },
        ].map((n) => {
          const active = network === n.k;
          return (
            <button
              key={n.k}
              type="button"
              onClick={() => setNetwork(n.k)}
              data-testid={`lookup-net-${n.k}`}
              className={`h-8 px-3 text-xs font-semibold rounded-full border transition-colors ${
                active
                  ? "bg-[#002FA7] text-white border-[#002FA7]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {n.label}
              <span className={`ml-1.5 font-mono text-[10px] ${active ? "text-white/80" : "text-slate-400"}`}>
                {netCounts[n.k] ?? 0}
              </span>
            </button>
          );
        })}
        <div className="relative ml-auto w-full sm:w-72">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search brand, advertiser, or ID…"
            className="h-9 pl-8 text-sm"
            data-testid="lookup-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ─── FILTER ROW ─── */}
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/40 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Country</Label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="h-9 text-sm" data-testid="lookup-country">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="ALL">All countries</SelectItem>
              {countries.map((c) => (
                <SelectItem key={c} value={c}>
                  {ISO_TO_DISPLAY[c] ? `${ISO_TO_DISPLAY[c]} (${c})` : c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Min CPA %</Label>
          <Input
            type="number"
            step="0.1"
            value={minCpa}
            onChange={(e) => setMinCpa(e.target.value)}
            placeholder="e.g. 3"
            className="h-9 text-sm font-mono"
            data-testid="lookup-mincpa"
          />
        </div>
        <div className="sm:col-span-2 flex items-end justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className="h-9 text-xs text-slate-500"
            data-testid="lookup-reset-filters"
          >
            Reset filters
          </Button>
        </div>
      </div>

      {/* AdMedia manual onboarding moved to the global Manual Brand
          drawer (Admin → Brands → "Add brand manually"). Keeps a
          single global creation surface; this tab is now a pure
          assignment view, identical to every other network's. */}

      {/* ─── RESULTS TABLE ─── */}
      <div className="max-h-[420px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr className="border-b border-slate-200">
              <th className="w-10 px-3 py-2 text-left">
                <Checkbox
                  checked={allVisibleSelected}
                  onCheckedChange={toggleAllVisible}
                  data-testid="lookup-select-all"
                  aria-label="Select all visible"
                />
              </th>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Brand
              </th>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Network
              </th>
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Payout
              </th>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Countries
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-sm text-slate-400">
                  No brands match these filters.
                </td>
              </tr>
            ) : (
              visible.map((b) => {
                const net = b.network || "impact";
                const meta = NETWORK_META[net] || {
                  label: net,
                  cls: "bg-slate-100 text-slate-700",
                };
                const checked = selectedIds.has(b.campaign_id);
                const disabled = isRowDisabled(b);
                // Render normalized country codes from BOTH legacy
                // `shipping_regions` and mrge's `user_geographical_locations`.
                const regions = Array.from(_brandCountryCodes(b)).sort();
                const isSkim = net === "skimlinks";
                const isMrge = net === "mrge";
                // Payout display: prefer the network's own display string
                // (e.g. mrge "Up to 8%" / "2-5%" / "32USD fixed" /
                // Skimlinks "$120 per sale") so the rich rate detail isn't
                // squashed to a single number. Fall back to the parsed
                // base_commission_pct for legacy Impact rows.
                const payoutDisplay =
                  isSkim || isMrge
                    ? b.payout_display || (b.base_commission_pct ? `${Number(b.base_commission_pct).toFixed(2)}%` : "See details")
                    : (b.payout_display
                        || (b.base_commission_pct
                          ? `${Number(b.base_commission_pct).toFixed(2)}%`
                          : "—"));
                return (
                  <tr
                    key={b.campaign_id}
                    className={`border-b border-slate-100 ${
                      disabled
                        ? "opacity-40 cursor-not-allowed bg-slate-50"
                        : `hover:bg-slate-50 cursor-pointer ${checked ? "bg-blue-50/40" : ""}`
                    }`}
                    onClick={() => !disabled && toggleOne(b.campaign_id)}
                    title={disabled ? `Clear selection to pick from ${meta.label}` : ""}
                    data-testid={`lookup-row-${b.campaign_id}`}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onCheckedChange={() => toggleOne(b.campaign_id)}
                        aria-label={`Select ${b.name}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{b.name || "—"}</div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        {b.campaign_id}
                        {b.advertiser_name && b.advertiser_name !== b.name ? ` · ${b.advertiser_name}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[13px] text-slate-700 whitespace-nowrap">
                      {payoutDisplay}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-500 max-w-[280px] truncate" title={regions.join(", ")}>
                      {regions.length === 0 ? "—" : regions.slice(0, 3).join(", ") + (regions.length > 3 ? ` +${regions.length - 3}` : "")}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {filtered.length > visible.length && !publisherId && (
          <div className="px-3 py-3 border-t border-slate-100 text-center text-xs text-amber-700 bg-amber-50/40" data-testid="lookup-row-cap-notice">
            Showing first <span className="font-mono font-semibold">{visible.length.toLocaleString()}</span>{" "}
            of <span className="font-mono font-semibold">{filtered.length.toLocaleString()}</span>{" "}
            matches — narrow filters or use search to drill in further.
          </div>
        )}
        {publisherId && remoteTotal > LOOKUP_PAGE_SIZE && (
          <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between text-xs text-slate-600">
            <span className="font-mono">
              {remoteTotal === 0
                ? "0"
                : `${(page - 1) * LOOKUP_PAGE_SIZE + 1}–${Math.min(page * LOOKUP_PAGE_SIZE, remoteTotal)}`}
              {" "}of {remoteTotal.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={page <= 1 || remoteLoading}
                onClick={() => setPage(page - 1)}
                data-testid="lookup-prev"
              >
                Prev
              </Button>
              <span className="font-mono mx-2">Page {page} / {lookupTotalPages}</span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={page >= lookupTotalPages || remoteLoading}
                onClick={() => setPage(page + 1)}
                data-testid="lookup-next"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ─── BULK ASSIGN BAR ─── */}
      <div className="px-5 py-4 border-t border-slate-200 bg-slate-50/60 space-y-3">
        {/* Mode selector — hidden for Skimlinks (always pass-through). */}
        {!isSkimlinksContext && (
          <div className="flex flex-wrap items-center gap-2" data-testid="lookup-mode-row">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mr-1">
              Payout model
            </span>
            {[
              { k: "fixed_pct", label: "Fixed %", hint: "Our% × Pub% × Split cap" },
              { k: "flat_cpa", label: "Flat CPA $", hint: "min(flat, network payout)" },
              { k: "passthrough", label: "Passthrough", hint: "Network payout × Split" },
              { k: "cpc", label: "CPC", hint: "Pub earns split of upstream CPC per click — no rate gates" },
            ].map((m) => {
              const active = effectiveMode === m.k;
              const forced = m.k === "cpc" && allSelectedAreCpc;
              return (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => !forced && setPayoutMode(m.k)}
                  data-testid={`lookup-mode-${m.k}`}
                  title={forced ? "All selected brands are CPC — mode is locked" : m.hint}
                  className={`h-8 px-3 text-xs font-semibold rounded-full border transition-colors ${
                    active
                      ? (m.k === "cpc"
                          ? "bg-teal-600 text-white border-teal-600"
                          : "bg-[#002FA7] text-white border-[#002FA7]")
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  } ${forced ? "cursor-default" : ""}`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Mode-specific input row */}
        {effectiveMode === "cpc" ? (
          <div className="grid grid-cols-12 gap-3 items-end">
            <div className="col-span-12 sm:col-span-4 space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                CPC label <span className="text-slate-400 font-normal normal-case">(optional, publisher-facing)</span>
              </Label>
              <Input
                value={cpcDisplay}
                onChange={(e) => setCpcDisplay(e.target.value)}
                placeholder="e.g. upto $0.25"
                className="h-9 text-sm font-mono"
                data-testid="lookup-cpc-display-input"
              />
            </div>
            <div className="col-span-6 sm:col-span-2 space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                Pub flat $ <span className="text-slate-400 font-normal normal-case">(optional)</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={flatAmount}
                onChange={(e) => setFlatAmount(e.target.value)}
                placeholder="e.g. 0.25"
                className="h-9 text-sm font-mono"
                data-testid="lookup-cpc-flat-input"
              />
            </div>
            <div className="col-span-6 sm:col-span-2 space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Split (pub)</Label>
              <Input
                type="number"
                step="1"
                value={splitPct}
                onChange={(e) => setSplitPct(e.target.value)}
                placeholder={String(defaultSplit)}
                className="h-9 text-sm font-mono"
                data-testid="lookup-split-input"
              />
            </div>
            <div className="col-span-12 sm:col-span-4 flex items-end gap-2">
              <Button
                onClick={() => doAssign("selected")}
                disabled={submitting || selectedCount === 0}
                className="bg-teal-600 hover:bg-teal-700 text-white h-9 flex-1"
                data-testid="lookup-assign-selected-btn"
              >
                {submitting ? "Assigning…" : `Assign Selected (${selectedCount})`}
              </Button>
              <Button
                onClick={() => doAssign("all-filtered")}
                disabled={submitting || filtered.length === 0}
                variant="outline"
                className="h-9"
                data-testid="lookup-assign-all-filtered-btn"
                title="Assign every brand matching the current filters"
              >
                All {filtered.length}
              </Button>
            </div>
          </div>
        ) : effectiveMode === "passthrough" ? (
          <div className="space-y-3">
            {/* Per-action payout breakdown — optional; blank uses brand catalog rates */}
            <div className="border border-slate-200 rounded-md p-3 bg-white space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-700 font-bold">
                  Payout Breakdown
                  <span className="ml-1.5 text-slate-400 font-normal normal-case">(optional)</span>
                </Label>
                <span className="text-[10px] text-slate-400">
                  paste from Impact's Payout Groups table — formats: 35%, 35%-45%, $0.10, $4-$16
                </span>
              </div>
              {payoutActions.map((row, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`payout-action-row-${i}`}>
                  <Input
                    placeholder="Action (e.g. Online Sale)"
                    value={row.action}
                    onChange={(e) => {
                      const next = [...payoutActions];
                      next[i] = { ...next[i], action: e.target.value };
                      setPayoutActions(next);
                    }}
                    className="col-span-5 h-8 text-sm"
                    data-testid={`payout-action-name-${i}`}
                  />
                  <Input
                    placeholder="Value (e.g. 35%-45%)"
                    value={row.value}
                    onChange={(e) => {
                      const next = [...payoutActions];
                      next[i] = { ...next[i], value: e.target.value };
                      setPayoutActions(next);
                    }}
                    className="col-span-4 h-8 text-sm font-mono"
                    data-testid={`payout-action-value-${i}`}
                  />
                  <Input
                    placeholder="Note (optional)"
                    value={row.note || ""}
                    onChange={(e) => {
                      const next = [...payoutActions];
                      next[i] = { ...next[i], note: e.target.value };
                      setPayoutActions(next);
                    }}
                    className="col-span-2 h-8 text-[11px]"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (payoutActions.length === 1) {
                        setPayoutActions([{ action: "", value: "", note: "" }]);
                      } else {
                        setPayoutActions(payoutActions.filter((_, idx) => idx !== i));
                      }
                    }}
                    className="col-span-1 text-slate-400 hover:text-rose-600 text-lg leading-none"
                    title="Remove row"
                    data-testid={`payout-action-remove-${i}`}
                  >×</button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setPayoutActions([...payoutActions, { action: "", value: "", note: "" }])}
                className="text-[11px] text-[#002FA7] hover:underline font-semibold"
                data-testid="payout-action-add-btn"
              >
                + Add another action
              </button>
              <div className="pt-2 border-t border-slate-100">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                  Headline (optional — shown to pub as the primary number)
                </Label>
                <Input
                  placeholder="e.g. 'up to 45%' — leave blank to auto-build from rows above"
                  value={headlinePayout}
                  onChange={(e) => setHeadlinePayout(e.target.value)}
                  className="h-8 text-sm mt-1"
                  data-testid="payout-headline-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-12 sm:col-span-3 space-y-1">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                  Split (pub)
                </Label>
                <Input
                  type="number"
                  step="1"
                  value={splitPct}
                  onChange={(e) => setSplitPct(e.target.value)}
                  placeholder={String(defaultSplit)}
                  className="h-9 text-sm font-mono"
                  data-testid="lookup-split-input"
                />
              </div>
              <div className="col-span-12 sm:col-span-9 flex items-end gap-2">
                <Button
                  onClick={() => doAssign("selected")}
                  disabled={submitting || selectedCount === 0}
                  className={`${isSkimlinksContext ? "bg-orange-600 hover:bg-orange-700" : "bg-[#002FA7] hover:bg-[#00227A]"} text-white h-9 flex-1`}
                  data-testid="lookup-assign-selected-btn"
                >
                  {submitting ? "Assigning…" : `Assign Selected (${selectedCount})`}
                </Button>
                <Button
                  onClick={() => doAssign("all-filtered")}
                  disabled={submitting || filtered.length === 0}
                  variant="outline"
                  className="h-9"
                  data-testid="lookup-assign-all-filtered-btn"
                  title="Assign every brand matching the current filters"
                >
                  All {filtered.length}
                </Button>
              </div>
            </div>
          </div>
        ) : effectiveMode === "flat_cpa" ? (
          <div className="grid grid-cols-12 gap-3 items-end">
            <div className="col-span-6 sm:col-span-3 space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                Flat CPA $ <span className="text-slate-400 font-normal normal-case">(per action)</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                value={flatAmount}
                onChange={(e) => setFlatAmount(e.target.value)}
                placeholder="e.g. 2.00"
                className="h-9 text-sm font-mono"
                data-testid="lookup-flat-input"
              />
            </div>
            <div className="col-span-6 sm:col-span-3 space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                Split (pub)
              </Label>
              <Input
                type="number"
                step="1"
                value={splitPct}
                onChange={(e) => setSplitPct(e.target.value)}
                placeholder={String(defaultSplit)}
                className="h-9 text-sm font-mono"
                data-testid="lookup-split-input"
              />
            </div>
            <div className="col-span-12 sm:col-span-6 flex items-end gap-2">
              <Button
                onClick={() => doAssign("selected")}
                disabled={submitting || selectedCount === 0}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white h-9 flex-1"
                data-testid="lookup-assign-selected-btn"
              >
                {submitting ? "Assigning…" : `Assign Selected (${selectedCount})`}
              </Button>
              <Button
                onClick={() => doAssign("all-filtered")}
                disabled={submitting || filtered.length === 0}
                variant="outline"
                className="h-9"
                data-testid="lookup-assign-all-filtered-btn"
                title="Assign every brand matching the current filters"
              >
                All {filtered.length}
              </Button>
            </div>
          </div>
        ) : (
          // fixed_pct
          <div className="grid grid-cols-12 gap-3 items-end">
            <div className="col-span-6 sm:col-span-3 space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                Our rate % <span className="text-slate-400 font-normal normal-case">(override)</span>
              </Label>
              <Input
                type="number"
                step="0.1"
                value={ourPct}
                onChange={(e) => setOurPct(e.target.value)}
                placeholder="Leave blank = brand's rate"
                className="h-9 text-sm font-mono"
                data-testid="lookup-our-input"
              />
            </div>
            <div className="col-span-6 sm:col-span-3 space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Pub rate %</Label>
              <Input
                type="number"
                step="0.1"
                value={pubPct}
                onChange={(e) => setPubPct(e.target.value)}
                placeholder="required"
                className="h-9 text-sm font-mono"
                data-testid="lookup-pub-input"
              />
            </div>
            <div className="col-span-6 sm:col-span-2 space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Split (pub)</Label>
              <Input
                type="number"
                step="1"
                value={splitPct}
                onChange={(e) => setSplitPct(e.target.value)}
                placeholder={String(defaultSplit)}
                className="h-9 text-sm font-mono"
                data-testid="lookup-split-input"
              />
            </div>
            <div className="col-span-12 sm:col-span-4 flex items-end gap-2">
              <Button
                onClick={() => doAssign("selected")}
                disabled={submitting || selectedCount === 0}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white h-9 flex-1"
                data-testid="lookup-assign-selected-btn"
              >
                {submitting ? "Assigning…" : `Assign Selected (${selectedCount})`}
              </Button>
              <Button
                onClick={() => doAssign("all-filtered")}
                disabled={submitting || filtered.length === 0}
                variant="outline"
                className="h-9"
                data-testid="lookup-assign-all-filtered-btn"
                title="Assign every brand matching the current filters"
              >
                All {filtered.length}
              </Button>
            </div>
          </div>
        )}

        {/* Mode helper copy */}
        {isSkimlinksContext ? (
          <p className="text-[11px] text-orange-700/80">
            <b>Skimlinks is pass-through.</b> Publisher earns <b>network payout × split</b> on whatever the offer pays — flat $, %, or tiered. No per-brand rate to set.
          </p>
        ) : effectiveMode === "cpc" ? (
          <p className="text-[11px] text-teal-700">
            <b>CPC offer.</b> Publisher earns <b>upstream CPC × split</b> on every click — no conversion required.
            Optional <b>Pub flat $</b> stores <span className="font-mono">pub_flat_amount</span> on the assignment.
            The label (e.g. <span className="font-mono">upto $0.25</span>) is publisher-facing only.
            {allSelectedAreCpc && (
              <span className="ml-1 text-teal-600/70">Auto-selected because all checked brands are CPC.</span>
            )}
          </p>
        ) : effectiveMode === "flat_cpa" ? (
          <p className="text-[11px] text-slate-500">
            <b>Flat CPA.</b> Publisher earns <b>${flatAmount || "—"}</b> per action (capped at the network&rsquo;s actual payout). Use this for flat-$ offers like <i>$2 per lead</i>.
          </p>
        ) : effectiveMode === "passthrough" ? (
          <p className="text-[11px] text-slate-500">
            <b>Pass-through.</b> Publisher earns <b>network payout × split</b> on whatever the network actually pays.
            Leave the breakdown blank to use each brand&apos;s catalog rate; fill rows only when you want to override.
          </p>
        ) : (
          <p className="text-[11px] text-slate-400">
            Tip: narrow by network / country / min-CPA, check the rows you want, then <b>Assign Selected</b>.
            Leave <b>Our rate %</b> blank to keep each brand&rsquo;s own advertised rate.
          </p>
        )}
        {selectedNetwork && (
          <p className="text-[11px] text-slate-500">
            Locked to <b>{(NETWORK_META[selectedNetwork] || NETWORK_META.impact).label}</b> network.{" "}
            <button type="button" onClick={clearSelection} className="underline hover:text-slate-800" data-testid="lookup-clear-selection">
              Clear selection
            </button>{" "}
            to pick from another network.
          </p>
        )}
      </div>
    </div>
  );
}
